import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  TablePagination,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import moment from "moment";
import { getPigmyCollectionsService } from "../../../../services/pigmyService";
import useStore from "../../../../useStore";
import CustomerPigmyDetails from "./CustomerPigmyDetails"; // Assuming you have this component

function CustomerPigmyCollection() {
  const { pigmyId } = useStore();
  const [pigmyCollections, setPigmyCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0); // current page
  const [rowsPerPage, setRowsPerPage] = useState(5); // number of rows per page
  const [totalRecords, setTotalRecords] = useState(0); // total count of records
  const [expanded, setExpanded] = useState({
    pigmyDetails: true,
    pigmyCollections: true,
  });

  useEffect(() => {
    // API call to fetch pigmy collections
    const fetchPigmyCollections = async () => {
      try {
        setLoading(true);
        const data = await getPigmyCollectionsService(
          pigmyId,
          page + 1,
          rowsPerPage
        );
        setPigmyCollections(data.pigmyCollections);
        setTotalRecords(data.totalCollections); // assuming you return total records from the API
        setLoading(false);
      } catch (error) {
        setError("Error fetching data");
        setLoading(false);
      }
    };

    fetchPigmyCollections();
  }, [pigmyId, page, rowsPerPage]);

  // Handle change in page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle change in number of rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  // Handle accordion change
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      {/* Accordion 1: Pigmy Details */}
      <Accordion
        expanded={expanded.pigmyDetails}
        onChange={handleAccordionChange("pigmyDetails")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Pigmy Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CustomerPigmyDetails />{" "}
          {/* Assuming you have a component for pigmy details */}
        </AccordionDetails>
      </Accordion>

      {/* Accordion 2: Pigmy Collections */}
      <Accordion
        expanded={expanded.pigmyCollections}
        onChange={handleAccordionChange("pigmyCollections")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Pigmy Collections</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper
            elevation={3}
            sx={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Pigmy Code
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Collection Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Due Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Collected Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Paid Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#3f51b5" }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pigmyCollections.map((collection, index) => (
                    <TableRow
                      key={collection?._id}
                      sx={{
                        backgroundColor: collection.highlight
                          ? "#ffcccc"
                          : "white",
                      }}
                    >
                      <TableCell>{collection.pigmy.pigmyCode}</TableCell>
                      <TableCell>{collection.collectionAmount}</TableCell>
                      <TableCell>
                        {moment(collection.dueDate).format("DD-MM-YYYY")}
                      </TableCell>
                      <TableCell>
                        {collection?.collectionDate
                          ? moment(collection?.collectionDate).format(
                              "DD-MM-YYYY"
                            )
                          : "-"}
                      </TableCell>
                      <TableCell>{collection.paidAmount}</TableCell>
                      <TableCell
                        sx={{
                          color:
                            collection.status === "Paid"
                              ? "green"
                              : collection.status === "Partial Payment"
                              ? "#8B8000"
                              : "red",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {collection.status === "Paid" ? (
                          <>
                            <CheckCircleIcon sx={{ mr: 1 }} /> Paid
                          </>
                        ) : collection.status === "Partial Payment" ? (
                          <>
                            <CancelIcon sx={{ mr: 1 }} /> Partial Payment
                          </>
                        ) : (
                          <>
                            <CancelIcon sx={{ mr: 1 }} /> Unpaid
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                backgroundColor: "#f5f5f5",
                borderTop: "1px solid #e0e0e0",
              }}
            />
          </Paper>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default CustomerPigmyCollection;
